import * as joint from '@joint/plus';

export function initializeSelection(service) {
  const { paperScroller, paper, keyboardService, inspectorService, haloService } = service;

  service.clipboard = new joint.ui.Clipboard();
  service.selection = new joint.ui.Selection({
    paper: paperScroller,
    useModelGeometry: false,
    translateConnectedLinks: joint.ui.Selection.ConnectedLinksTranslation.SUBGRAPH,
    handles: [],
    filter: (cellView) => cellView?.model?.isElement(),
  });

  service.selection.collection.on('reset add remove', onSelectionChange);
  const keyboard = keyboardService.keyboard;

  if (!service.readOnly) {
    paper.on('blank:pointerdown', (evt) => {
      if (inspectorService.isSelectingDestination) return;
      if (keyboard.isActive('shift', evt) || evt.button === 0) {
        service.selection.startSelecting(evt);
      } else {
        service.selection.collection.reset([]);
        paperScroller.startPanning(evt);
        paper.removeTools();
      }
    });

    paper.on('link:pointerdown', (linkView, evt) => {
      evt.stopPropagation();
      selectPrimaryLink(linkView);
      inspectorService.create(linkView.model, service.graph, paper, service.modules);
    });

    let downPoint = null;
    paper.on('element:pointerdown', (elementView, evt) => {
      if (inspectorService.isSelectingDestination) return;
      downPoint = { x: evt.clientX, y: evt.clientY };
    });

    paper.on('element:pointermove', (elementView, evt) => {
      if (!downPoint) return;
      const dx = Math.abs(evt.clientX - downPoint.x);
      const dy = Math.abs(evt.clientY - downPoint.y);
      const moved = dx + dy > 3;
      if (!moved) evt.stopPropagation();
    });

    paper.on('element:pointerup', () => {
      downPoint = null;
    });

    paper.on('cell:pointerdblclick', (cellView, evt) => {
      if (inspectorService.isSelectingDestination) return;
      const cell = cellView.model;
      if (!cell.isElement()) return;
      evt.preventDefault();
      evt.stopPropagation();
      const toggle = keyboard.isActive('ctrl meta shift', evt);
      const coll = service.selection.collection;
      if (toggle) {
        if (coll.has(cell)) coll.remove(cell);
        else coll.add(cell);
      } else {
        coll.reset([cell]);
      }
    });
  } else {
    paper.on('blank:pointerdown', (evt) => {
      if (inspectorService.isSelectingDestination) return;
      service.selection.collection.reset([]);
      paperScroller.startPanning(evt);
    });
  }

  service.graph.on('remove', (cell) => {
    if (service.selection.collection.has(cell)) {
      service.selection.collection.reset(service.selection.collection.models.filter((c) => c !== cell));
    }
  });

  service.selection.on('selection-box:pointerdown', (elementView, evt) => {
    if (keyboard.isActive('ctrl meta', evt)) {
      service.selection.collection.remove(elementView.model);
    }
  });

  service.selection.on('selection-box:pointerup', (_, evt) => {
    if (evt.button === 2) {
      evt.stopPropagation();
      service.renderContextToolbar({ x: evt.clientX, y: evt.clientY }, service.selection.collection.toArray());
    }
  });

  function onSelectionChange() {
    if (inspectorService.isSelectingDestination) return;
    paper.removeTools();
    joint.ui.Halo.clear(paper);
    joint.ui.FreeTransform.clear(paper);
    const { collection } = service.selection;
    if (collection.length === 1) {
      const primaryCell = collection.first();
      const primaryView = paper.findViewByModel(primaryCell);
      if (primaryCell.isElement()) selectPrimaryCell(primaryView);
    }
  }

  function selectPrimaryCell(cellView) {
    const cell = cellView.model;
    if (cell.isElement()) selectPrimaryElement(cellView);
    else selectPrimaryLink(cellView);
  }

  function selectPrimaryElement(elementView) {
    const element = elementView.model;
    if (!service.readOnly) {
      new joint.ui.FreeTransform({
        cellView: elementView,
        allowRotation: false,
        preserveAspectRatio: !!element.get('preserveAspectRatio'),
        alloworthogonalResize: element.get('alloworthogonalResize') !== false,
        useBordersToResize: true,
      }).render();
      haloService.create(elementView);
    }
  }

  function selectPrimaryLink(linkView) {
    if (linkView.hasTools('link-tools')) return;
    const ns = joint.linkTools;
    const toolsView = new joint.dia.ToolsView({
      name: 'link-tools',
      tools: [
        new ns.Vertices({ snapRadius: 20, redundancyRemoval: true, vertexAdding: true }),
        new ns.Segments({ snapRadius: 20, snapHandle: true, redundancyRemoval: true }),
        new ns.TargetArrowhead(),
        new ns.Boundary({ padding: 15 }),
      ],
    });
    toolsView.el.style.pointerEvents = 'all';
    linkView.addTools(toolsView);
    linkView.model.set('interactive', {
      vertexAdd: true,
      vertexMove: true,
      vertexRemove: true,
      arrowheadMove: true,
      labelMove: true,
      useLinkTools: true,
      addVertex: true,
      removeVertex: true,
    });
    linkView.model.router('normal', {
      padding: 10,
      useVertices: true,
      directions: ['top', 'right', 'bottom', 'left'],
    });
  }
}
